/**
 * Compress/resize an image File in the browser before upload.
 * Large phone photos (3-8 MB) are shrunk to ~200-500 KB, dramatically
 * reducing upload time. Non-image files (PDF/Word/Excel/CSV) are returned
 * unchanged. If anything fails (e.g. HEIC the browser can't decode), the
 * original file is returned so the upload still works.
 *
 * @param {File} file
 * @param {number} maxDim - max width/height in px (default 1600)
 * @param {number} quality - JPEG quality 0-1 (default 0.7)
 * @returns {Promise<File>}
 */
export function compressImageFile(file, maxDim = 1600, quality = 0.7) {
  return new Promise((resolve) => {
    if (!file || !file.type || !file.type.startsWith('image/')) {
      resolve(file);
      return;
    }
    // Tiny images aren't worth compressing
    if (file.size < 400 * 1024) {
      resolve(file);
      return;
    }

    const url = URL.createObjectURL(file);
    const img = new Image();
    const fail = () => { try { URL.revokeObjectURL(url); } catch (e) {} resolve(file); };

    img.onload = () => {
      URL.revokeObjectURL(url);
      try {
        let w = img.width;
        let h = img.height;
        if (w > maxDim || h > maxDim) {
          const scale = maxDim / Math.max(w, h);
          w = Math.round(w * scale);
          h = Math.round(h * scale);
        }
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        canvas.toBlob(
          (blob) => {
            if (!blob || blob.size >= file.size) {
              resolve(file); // compression didn't help
              return;
            }
            const newName = (file.name || 'bill').replace(/\.(png|webp|heic|heif|gif|jpe?g)$/i, '') + '.jpg';
            resolve(new File([blob], newName, { type: 'image/jpeg', lastModified: Date.now() }));
          },
          'image/jpeg',
          quality
        );
      } catch (e) {
        resolve(file);
      }
    };
    img.onerror = fail;
    img.src = url;
  });
}
